import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Globe, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { LANGS } from "@/i18n/translations";

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const sections = [
    { id: "services", label: t.nav.services },
    { id: "industries", label: t.nav.industries },
    { id: "work", label: t.nav.work },
    { id: "solutions", label: t.nav.solutions },
    { id: "process", label: t.nav.process },
    { id: "about", label: t.nav.about },
    { id: "global", label: t.nav.global },
    { id: "contact", label: t.nav.contact },
  ];

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <nav
      data-testid="bbm-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-shadow ${
        scrolled ? "bbm-glass shadow-sm" : "bbm-glass"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" data-testid="bbm-logo" className="flex items-center gap-3">
          <img
            src="/bbm-logo.png"
            alt="BBM — Best Business Management"
            className="h-10 w-auto object-contain"
          />
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-8">
          {sections.map((s) => (
            <button
              key={s.id}
              data-testid={`nav-link-${s.id}`}
              onClick={() => scrollTo(s.id)}
              className="text-sm text-[#4B5563] hover:text-[#0B132B] transition-colors font-medium"
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Right cluster */}
        <div className="flex items-center gap-3">
          {/* Language switcher */}
          <div className="hidden sm:flex items-center gap-1 border border-[#E5E7EB] rounded-md p-0.5" data-testid="lang-switcher">
            <Globe className="w-3.5 h-3.5 ms-1.5 me-0.5 text-[#4B5563]" />
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-${l.code}`}
                onClick={() => setLang(l.code)}
                className={`px-2 py-1 text-xs font-mono rounded-sm transition-colors ${
                  lang === l.code
                    ? "bg-[#0B132B] text-white"
                    : "text-[#4B5563] hover:text-[#0B132B]"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <button
            data-testid="nav-cta-btn"
            onClick={() => scrollTo("contact")}
            className="bbm-btn-primary !py-2 !px-4 text-sm hidden md:inline-flex"
          >
            {t.nav.cta}
            <ArrowUpRight className="w-4 h-4" />
          </button>

          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden p-2 text-[#0B132B]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-[#E5E7EB] bg-white" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-1">
            {sections.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                className="text-start py-3 text-[#0B132B] font-medium border-b border-[#F3F4F6]"
                data-testid={`mobile-nav-${s.id}`}
              >
                {s.label}
              </button>
            ))}
            <div className="flex items-center gap-2 mt-3">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-1.5 text-xs font-mono rounded-sm border ${
                    lang === l.code
                      ? "bg-[#0B132B] text-white border-[#0B132B]"
                      : "text-[#4B5563] border-[#E5E7EB]"
                  }`}
                  data-testid={`mobile-lang-${l.code}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <button
              data-testid="mobile-cta-btn"
              onClick={() => scrollTo("contact")}
              className="bbm-btn-primary mt-4 justify-center"
            >
              {t.nav.cta}
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
